import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  selectRecipeById,
  selectIngredientsByRecipeId,
  selectMethodStepsByRecipeId,
  selectActiveProgressByRecipeId,
  selectCookMode,
  selectUnitSystem,
  selectAutoShowTimer,
  selectTimerSound,
  selectTimerFlash,
  toggleCookMode,
  updateCookingProgress,
  abandonCookingProgress,
  getQuantityString,
  formatMultiplier,
  setRating,
  useT,
  useAppDispatch,
  useAppSelector,
  type RootState,
} from "@reciperoll/core";
import { X, ArrowLeft, Play, Pause, RotateCcw, ChevronDown, Sun, Moon, FileText, Clock } from "react-feather";
import { useToast } from "../components/Toast";
import { StarRating } from "../components/StarRating";

const ACCENT = "#E07A5F";

export default function CookMode() {
  const { id } = useParams<{ id: string }>();
  const recipeId = id ?? "";
  const t = useT();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const toast = useToast();

  const recipe = useAppSelector((s: RootState) => selectRecipeById(s, recipeId));
  const ingredients = useAppSelector((s) => selectIngredientsByRecipeId(s, recipeId));
  const methodSteps = useAppSelector((s) => selectMethodStepsByRecipeId(s, recipeId));
  const cookingProgress = useAppSelector((s) =>
    selectActiveProgressByRecipeId(s, recipeId),
  );

  // Sidebar rows currently showing their substitute instead of the main
  // ingredient. View-local: a swap is a per-cook choice, not an edit.
  const [swappedIngredients, setSwappedIngredients] = useState<Record<string, boolean>>({});

  // Keep the screen awake while cooking (supported browsers only). The lock is
  // dropped when the tab is hidden, so re-acquire it when the page is shown again.
  useEffect(() => {
    const nav = navigator as Navigator & {
      wakeLock?: { request: (t: "screen") => Promise<{ release: () => Promise<void> }> };
    };
    if (!nav.wakeLock) return;
    let lock: { release: () => Promise<void> } | null = null;
    let cancelled = false;
    const acquire = async () => {
      try {
        lock = await nav.wakeLock!.request("screen");
      } catch {
        /* denied or not permitted (e.g. tab not focused) — ignore */
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible" && !cancelled) acquire();
    };
    acquire();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibility);
      lock?.release().catch(() => {});
    };
  }, []);

  // Cook-screen theme is its own persisted preference (default "dark") — it does
  // NOT follow the app/system theme, so cook mode opens black regardless, and the
  // sun/moon button flips it. Driven by explicit classes (not `dark:` variants).
  const cookMode = useAppSelector(selectCookMode);
  const unitSystem = useAppSelector(selectUnitSystem);
  const light = cookMode === "light";
  // The timer is always available. "Auto show timer" only sets whether it starts
  // expanded or collapsed to a clock icon; the cook can toggle either way with the
  // X / icon. Resets each cook-mode entry.
  const autoShowTimer = useAppSelector(selectAutoShowTimer);
  const [timerCollapsed, setTimerCollapsed] = useState(!autoShowTimer);

  const shell = `h-screen ${light ? "bg-white text-stone-800" : "bg-black text-white"} flex flex-col overflow-hidden`;
  const iconBtn = light ? "bg-stone-100 hover:bg-stone-200" : "bg-white/10 hover:bg-white/20";
  const softBtn = light
    ? "bg-stone-100 hover:bg-stone-200 text-stone-600"
    : "bg-white/10 hover:bg-white/20 text-white/70";
  const card = light ? "bg-stone-100" : "bg-white/[0.06]";
  const muted = light ? "text-stone-400" : "text-white/40";
  const headerIcon = light ? "text-stone-600" : "text-white/70";
  const track = light ? "bg-stone-200" : "bg-white/10";
  const kbdBg = light ? "bg-stone-200" : "bg-white/10";

  // Section headings aren't cookable steps — the stepper walks real steps only.
  const steps = methodSteps.filter((s) => !s.group);
  const totalSteps = steps.length;
  const scale = cookingProgress?.servingMultiplier ?? 1;

  // Step position is LOCAL state so Next/Previous update instantly; persisted to
  // Redux debounced during the session and flushed on exit (mirrors the RN cook
  // screen), so rapid stepping doesn't pay the dispatch/persist cost each press.
  const [currentStep, setCurrentStep] = useState(() => cookingProgress?.currentStep ?? 1);

  const currentStepRef = useRef(currentStep);
  currentStepRef.current = currentStep;
  const totalStepsRef = useRef(totalSteps);
  totalStepsRef.current = totalSteps;
  const lastStoredStep = useRef(cookingProgress?.currentStep ?? 1);

  const handlePrevious = useCallback(() => setCurrentStep((s) => (s > 1 ? s - 1 : s)), []);
  const handleNext = useCallback(
    () => setCurrentStep((s) => (s < totalStepsRef.current ? s + 1 : s)),
    [],
  );

  // Adopt external step changes (rehydrate / remote sync) without clobbering
  // in-progress local navigation.
  useEffect(() => {
    const stored = cookingProgress?.currentStep ?? 1;
    if (stored !== lastStoredStep.current) {
      lastStoredStep.current = stored;
      setCurrentStep(stored);
    }
  }, [cookingProgress?.currentStep]);

  // Debounced persist after the user settles.
  useEffect(() => {
    if (currentStep === lastStoredStep.current) return;
    const t = setTimeout(() => {
      lastStoredStep.current = currentStep;
      dispatch(updateCookingProgress({ recipeId, currentStep }));
    }, 500);
    return () => clearTimeout(t);
  }, [currentStep, dispatch, recipeId]);

  // Flush the final step on exit (left before the debounce fired).
  useEffect(
    () => () => {
      if (currentStepRef.current !== lastStoredStep.current) {
        dispatch(updateCookingProgress({ recipeId, currentStep: currentStepRef.current }));
      }
    },
    [dispatch, recipeId],
  );

  // Keyboard: ←/→ navigate steps.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") handlePrevious();
      else if (e.key === "ArrowRight") handleNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [handleNext, handlePrevious]);

  const [confirmAction, setConfirmAction] = useState<null | "close" | "stop">(null);
  const [showNotes, setShowNotes] = useState(false);
  const recipeNotes = recipe?.notes?.trim() ?? "";

  // Leaving cook mode returns to the recipe we came from. Going *back* (rather
  // than pushing /recipe/:id) keeps cook mode out of the recipe's Back-button
  // history, so the recipe's Back returns to wherever it was opened from (home,
  // a collection, …) and never to cook mode. Fall back to the recipe route only
  // when there's no history to pop (deep link / hard refresh on the cook URL).
  const exitToRecipe = () => {
    if ((window.history.state?.idx ?? 0) > 0) navigate(-1);
    else navigate(`/recipe/${recipeId}`, { replace: true });
  };

  // Finishing marks the cook complete, then shows a celebration + rating prompt
  // (rating optional); leaving the prompt returns to the recipe.
  const [finishing, setFinishing] = useState(false);
  const handleFinish = () => {
    dispatch(updateCookingProgress({ recipeId, status: "completed" }));
    setFinishing(true);
  };
  const doFinishDone = () => {
    setFinishing(false);
    toast.show(recipe?.title ? t("cook.completed", { title: recipe.title }) : t("cook.cookComplete"));
    exitToRecipe();
  };
  const doClose = () => exitToRecipe(); // progress flushes on unmount
  const doStop = () => {
    dispatch(abandonCookingProgress(recipeId));
    if (recipe?.title) toast.show(t("cook.stoppedCooking", { title: recipe.title }));
    exitToRecipe();
  };

  const confirmModal =
    confirmAction === "close" ? (
      <ConfirmModal
        title={t("cook.breakTitle")}
        message={t("cook.breakMessage")}
        confirmLabel={t("cook.takeBreak")}
        cancelLabel={t("cook.keepCooking")}
        onConfirm={doClose}
        onCancel={() => setConfirmAction(null)}
      />
    ) : confirmAction === "stop" ? (
      <ConfirmModal
        title={t("cook.stopTitle")}
        message={t("cook.stopMessage")}
        confirmLabel={t("recipe.stopCooking")}
        confirmClass="bg-red-500 hover:bg-red-600"
        cancelLabel={t("common.cancel")}
        onConfirm={doStop}
        onCancel={() => setConfirmAction(null)}
      />
    ) : null;

  const currentMethodStep = steps[currentStep - 1];
  const stepText = currentMethodStep?.instruction ?? "";

  // Nearest heading before this step, for on-screen context.
  const currentSection = (() => {
    if (!currentMethodStep) return undefined;
    const fullIdx = methodSteps.findIndex((s) => s.id === currentMethodStep.id);
    for (let i = fullIdx - 1; i >= 0; i--) {
      if (methodSteps[i].group) return methodSteps[i].instruction;
    }
    return undefined;
  })();

  // Per-group step numbers (restart after each heading) for the step label.
  const stepNumbers: Record<string, number> = {};
  let g = 0;
  for (const s of methodSteps) {
    if (s.group) {
      g = 0;
      continue;
    }
    g += 1;
    stepNumbers[s.id] = g;
  }
  const currentStepLabel = currentMethodStep ? stepNumbers[currentMethodStep.id] : currentStep;
  const progressPct = totalSteps ? (currentStep / totalSteps) * 100 : 0;

  const header = (
    <div className="flex justify-between items-center mb-4">
      <button
        onClick={() => setConfirmAction("close")}
        className={`w-9 h-9 cursor-pointer rounded-xl ${iconBtn} transition-colors flex items-center justify-center`}
        aria-label={t("common.back")}
      >
        <ArrowLeft className={`w-5 h-5 ${headerIcon}`} />
      </button>
      <p className={`text-sm font-semibold ${muted} truncate px-4`}>{recipe?.title}</p>
      <div className="flex items-center gap-2">
        <button
          onClick={() => dispatch(toggleCookMode())}
          className={`w-9 h-9 cursor-pointer rounded-xl ${iconBtn} transition-colors flex items-center justify-center`}
          aria-label={light ? t("cook.switchToDark") : t("cook.switchToLight")}
        >
          {light ? <Moon className={`w-5 h-5 ${headerIcon}`} /> : <Sun className={`w-5 h-5 ${headerIcon}`} />}
        </button>
        <button
          onClick={() => setConfirmAction("stop")}
          className={`w-9 h-9 cursor-pointer rounded-xl ${iconBtn} transition-colors flex items-center justify-center`}
          aria-label={t("recipe.stopCooking")}
        >
          <X className="w-5 h-5 text-red-500" />
        </button>
      </div>
    </div>
  );

  if (!totalSteps) {
    return (
      <div className={shell}>
        <div className="flex-1 flex flex-col min-h-0 px-6 lg:px-10 py-6">
          {header}
          <div className="flex-1 flex flex-col items-center justify-center text-center">
            <p className="text-xl font-bold mb-2">{t("form.noStepsYet")}</p>
            <p className={muted}>{t("cook.noStepsMessage")}</p>
          </div>
        </div>
        {confirmModal}
      </div>
    );
  }

  return (
    <div className={shell}>
      <div className="flex-1 flex flex-col min-h-0 px-6 lg:px-10 py-6">
        {header}

        <div className={`h-1 ${track} rounded-full mb-6 shrink-0`}>
          <div className="h-full rounded-full transition-all" style={{ width: `${progressPct}%`, background: ACCENT }} />
        </div>

        <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-10">
          {/* Step */}
          <div className="flex flex-col justify-center min-h-0 overflow-y-auto">
            {currentSection && (
              <p className={`text-sm font-semibold uppercase tracking-wider ${muted} mb-1`}>{currentSection}</p>
            )}
            <div className="flex items-baseline gap-2.5 mb-5">
              <p className="text-xs font-bold uppercase tracking-[1.5px]" style={{ color: ACCENT }}>
                {t("cook.step", { number: currentStepLabel })}
              </p>
              <span className={`text-xs font-bold ${muted}`}>·</span>
              <p className={`text-xs font-bold ${muted}`}>{currentStep}/{totalSteps}</p>
            </div>
            <p className="font-display text-3xl lg:text-4xl font-bold leading-snug max-w-3xl">
              {stepText}
            </p>
          </div>

          {/* Sidebar: timer + scaled ingredients */}
          <div className="flex flex-col gap-5 min-h-0">
            {timerCollapsed ? (
              <button
                onClick={() => setTimerCollapsed(false)}
                aria-label={t("cook.showTimer")}
                className={`${light ? "bg-stone-100 hover:bg-stone-200" : "bg-white/[0.06] hover:bg-white/10"} self-start rounded-2xl py-2.5 px-4 flex items-center gap-2 text-sm font-bold text-terracotta cursor-pointer transition-colors shrink-0`}
              >
                <Clock className="w-4 h-4" />
                {t("cook.timer")}
              </button>
            ) : (
              <CookTimer
                light={light}
                initialSeconds={recipe?.cookingDuration || 300}
                onCollapse={() => setTimerCollapsed(true)}
              />
            )}

            {recipeNotes && (
              <button
                onClick={() => setShowNotes(true)}
                className={`${softBtn} transition-colors rounded-2xl py-3 px-4 flex items-center justify-center gap-2 text-sm font-bold cursor-pointer shrink-0`}
              >
                <FileText className="w-4 h-4" />
                {t("recipe.notes")}
              </button>
            )}

            <div className={`${card} rounded-2xl p-5 flex-1 min-h-0 flex flex-col`}>
              <p className={`text-xs font-bold uppercase tracking-wide ${muted} mb-3 shrink-0`}>
                {scale !== 1
                  ? t("cook.ingredientsScaled", { multiplier: formatMultiplier(scale) })
                  : t("recipe.ingredients")}
              </p>
              {ingredients.length === 0 ? (
                <p className={`text-sm ${muted}`}>{t("cook.noIngredientsListed")}</p>
              ) : (
                <IngredientsScroll light={light}>
                  {ingredients.map((ing) =>
                    ing.group ? (
                      <p key={ing.id} className={`text-[11px] font-bold uppercase tracking-wide ${muted} mt-2`}>
                        {ing.name}
                      </p>
                    ) : (
                      (() => {
                        // When swapped, the row shows the substitute; the swap
                        // button previews whichever one is NOT showing.
                        const isSwapped = !!swappedIngredients[ing.id] && !!ing.substitute;
                        const active = isSwapped ? ing.substitute! : ing;
                        const inactive = isSwapped ? ing : ing.substitute;
                        return (
                          <div key={ing.id}>
                            <div className="flex justify-between gap-3">
                              <span className={`text-sm ${light ? "text-stone-600" : "text-white/70"}`}>{active.name}</span>
                              <span className="text-sm font-semibold whitespace-nowrap" style={{ color: ACCENT }}>
                                {getQuantityString(active, scale, unitSystem, t)}
                              </span>
                            </div>
                            {inactive ? (
                              <button
                                type="button"
                                aria-label={t("recipe.swapSubstitute")}
                                onClick={() =>
                                  setSwappedIngredients((prev) => ({ ...prev, [ing.id]: !prev[ing.id] }))
                                }
                                className={`text-xs inline-flex items-center gap-1 cursor-pointer ${light ? "text-stone-400 hover:text-stone-600" : "text-white/40 hover:text-white/70"}`}
                              >
                                <span aria-hidden>⇄</span>
                                {t("recipe.orSubstitute", {
                                  substitute: [
                                    getQuantityString(inactive, scale, unitSystem, t),
                                    inactive.name,
                                  ]
                                    .filter(Boolean)
                                    .join(" "),
                                })}
                              </button>
                            ) : null}
                          </div>
                        );
                      })()
                    ),
                  )}
                </IngredientsScroll>
              )}
            </div>
          </div>
        </div>

        <div className="mt-6 shrink-0">
          <p className={`hidden lg:flex items-center justify-center gap-2 text-xs ${light ? "text-stone-400" : "text-white/30"} font-medium mb-3`}>
            <kbd className={`${kbdBg} rounded px-2 py-1 font-mono`}>←</kbd>
            <kbd className={`${kbdBg} rounded px-2 py-1 font-mono`}>→</kbd>
            {t("cook.toNavigateSteps")}
          </p>
          <div className="flex gap-3 max-w-md mx-auto">
            <button
              onClick={handlePrevious}
              disabled={currentStep <= 1}
              className={`flex-1 ${softBtn} transition-colors rounded-2xl py-4 text-base font-bold disabled:opacity-30 cursor-pointer disabled:cursor-default`}
            >
              ← {t("cook.previous")}
            </button>
            <button
              onClick={currentStep === totalSteps ? handleFinish : handleNext}
              className="flex-1 rounded-2xl py-4 text-base font-bold text-white transition-colors cursor-pointer"
              style={{ background: ACCENT }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "#C45C3F")}
              onMouseLeave={(e) => (e.currentTarget.style.background = ACCENT)}
            >
              {currentStep === totalSteps ? `${t("cook.finish")} ✓` : `${t("cook.next")} →`}
            </button>
          </div>
        </div>
      </div>
      {confirmModal}
      {showNotes && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setShowNotes(false)}
        >
          <div
            className={`w-full max-w-md max-h-[80vh] flex flex-col rounded-2xl p-6 ${light ? "bg-white text-stone-800" : "bg-stone-900 text-white"}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4 shrink-0">
              <h2 className="text-xl font-bold">{t("recipe.notes")}</h2>
              <button
                onClick={() => setShowNotes(false)}
                className={`w-9 h-9 cursor-pointer rounded-xl ${iconBtn} transition-colors flex items-center justify-center`}
                aria-label={t("common.close")}
              >
                <X className={`w-5 h-5 ${headerIcon}`} />
              </button>
            </div>
            <p className="text-base leading-relaxed whitespace-pre-wrap overflow-y-auto">
              {recipeNotes}
            </p>
          </div>
        </div>
      )}
      {finishing && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={doFinishDone}
        >
          <div
            className="w-full max-w-sm rounded-2xl bg-white p-6 flex flex-col items-center text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-xl font-bold text-stone-800 mb-1">{t("cook.niceWork")}</h2>
            <p className="text-sm text-stone-500 mb-5">{t("cook.howWas", { title: recipe?.title })}</p>
            <StarRating
              value={recipe?.rating}
              onChange={(r) => recipe && dispatch(setRating({ id: recipe.id, rating: r }))}
              className="w-8 h-8"
            />
            <button
              onClick={doFinishDone}
              className="mt-6 w-full rounded-xl bg-terracotta hover:bg-terracotta-dark py-3 font-semibold text-white cursor-pointer"
            >
              {t("common.done")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Styled confirm dialog matching the start-cooking scaling modal. Kept a neutral
// light dialog — it reads fine over either cook theme.
function ConfirmModal({
  title,
  message,
  confirmLabel,
  cancelLabel,
  confirmClass = "bg-terracotta hover:bg-terracotta-dark",
  onConfirm,
  onCancel,
}: {
  title: string;
  message: string;
  confirmLabel: string;
  cancelLabel?: string;
  confirmClass?: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const t = useT();
  const cancel = cancelLabel ?? t("cook.keepCooking");
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onCancel}
    >
      <div
        className="w-full max-w-sm rounded-2xl bg-white p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-xl font-bold text-stone-800 mb-1">{title}</h2>
        <p className="text-sm text-stone-500 mb-6">{message}</p>
        <div className="flex gap-2">
          <button
            onClick={onCancel}
            className="flex-1 rounded-xl border border-stone-200 py-3 font-semibold text-stone-500 hover:bg-stone-50 cursor-pointer"
          >
            {cancel}
          </button>
          <button
            onClick={onConfirm}
            className={`flex-1 rounded-xl py-3 font-semibold text-white transition-colors cursor-pointer ${confirmClass}`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// How the timer-finished alarm repeats: a chime every ~2.5s, ~13 times (~30s),
// then stops (or stops earlier when the cook resets/adjusts the timer). Kept in
// sync with the mobile cook screen.
const TIMER_ALERT_INTERVAL_MS = 2500;
const TIMER_ALERT_REPEATS = 13;

// Standalone countdown timer — no per-step duration data exists, so it's a manual
// timer the cook sets. Play/pause, reset, and ±30s (while paused). On finish it
// beeps (Web Audio) and/or flashes, per preferences.
function CookTimer({ light, initialSeconds, onCollapse }: { light: boolean; initialSeconds: number; onCollapse: () => void }) {
  const t = useT();
  const timerSound = useAppSelector(selectTimerSound);
  const timerFlash = useAppSelector(selectTimerFlash);
  const [duration, setDuration] = useState(initialSeconds);
  const [remaining, setRemaining] = useState(initialSeconds);
  const [running, setRunning] = useState(false);
  const [flashOn, setFlashOn] = useState(false);
  // True only when the countdown actually elapsed to 0 (not when adjusting down to
  // 0 on a paused/never-started timer). Gates the alarm + flash.
  const [finished, setFinished] = useState(false);
  const audioCtxRef = useRef<AudioContext | null>(null);

  // Lazily create/resume the AudioContext on a user gesture (the play tap) so a
  // later beep — which fires with no direct gesture — isn't blocked by autoplay.
  const ensureAudio = () => {
    try {
      if (!audioCtxRef.current) {
        const Ctx =
          window.AudioContext ||
          (window as unknown as { webkitAudioContext: typeof AudioContext })
            .webkitAudioContext;
        audioCtxRef.current = new Ctx();
      }
      if (audioCtxRef.current.state === "suspended") audioCtxRef.current.resume();
    } catch {
      // Web Audio unavailable — beep just won't play.
    }
    return audioCtxRef.current;
  };

  // A soft two-note chime (C5 → E5) with a gentle bell-like decay — matches the
  // mobile timer-done.wav tone. Pure sine + a long soft tail, not sharp beeps.
  const beep = () => {
    const ctx = audioCtxRef.current;
    if (!ctx) return;
    const now = ctx.currentTime;
    ([
      [523.25, 0],
      [659.25, 0.32],
    ] as const).forEach(([freq, offset]) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      osc.connect(gain);
      gain.connect(ctx.destination);
      const t0 = now + offset;
      gain.gain.setValueAtTime(0.0001, t0);
      gain.gain.exponentialRampToValueAtTime(0.22, t0 + 0.015); // gentle onset
      gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.9); // long soft tail
      osc.start(t0);
      osc.stop(t0 + 0.95);
    });
  };

  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) {
          setRunning(false);
          setFinished(true); // counted down to 0 → alarm
          return 0;
        }
        return r - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [running]);

  // When the timer reaches zero, chime repeatedly like an alarm rather than once —
  // every TIMER_ALERT_INTERVAL_MS up to TIMER_ALERT_REPEATS, stopping when the timer
  // leaves zero (reset/adjust clears the interval via cleanup).
  useEffect(() => {
    if (!finished || !timerSound) return;
    beep(); // immediately on hitting zero
    let played = 1;
    const id = setInterval(() => {
      beep();
      if (++played >= TIMER_ALERT_REPEATS) clearInterval(id);
    }, TIMER_ALERT_INTERVAL_MS);
    return () => clearInterval(id);
  }, [finished, timerSound]);

  // Flash the card while finished (until reset/adjusted).
  useEffect(() => {
    if (!finished || !timerFlash) {
      setFlashOn(false);
      return;
    }
    const id = setInterval(() => setFlashOn((f) => !f), 600);
    return () => clearInterval(id);
  }, [finished, timerFlash]);

  // Mirror duration into a ref so press-and-hold repeats accumulate correctly
  // (a setTimeout loop would otherwise read a stale `duration` closure).
  const durationRef = useRef(duration);
  durationRef.current = duration;
  const holdRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Guards against a runaway repeat: a tick that fires just after release (or a
  // second press before release, e.g. a missed pointerup) must not keep the chain
  // alive. Only ticks while true, and there is only ever one chain.
  const holdActiveRef = useRef(false);

  const adjust = (delta: number) => {
    if (running) return;
    const d = Math.max(0, durationRef.current + delta);
    durationRef.current = d;
    setDuration(d);
    setRemaining(d);
    setFinished(false); // manual adjust is never an alarm
  };

  const stopAdjust = () => {
    holdActiveRef.current = false;
    if (holdRef.current) {
      clearTimeout(holdRef.current);
      holdRef.current = null;
    }
  };

  // Press-and-hold ±30: one step immediately, then after a short pause it
  // auto-repeats, accelerating (the interval shrinks) the longer it's held.
  const startAdjust = (delta: number) => {
    stopAdjust(); // cancel any existing chain so presses can't stack up
    holdActiveRef.current = true;
    adjust(delta);
    let delay = 260;
    const tick = () => {
      if (!holdActiveRef.current) return; // released between scheduling and firing
      adjust(delta);
      delay = Math.max(55, Math.round(delay * 0.8));
      if (holdActiveRef.current) holdRef.current = setTimeout(tick, delay);
    };
    holdRef.current = setTimeout(tick, 500); // pause before auto-repeat kicks in
  };
  useEffect(() => stopAdjust, []); // clear a pending repeat on unmount

  const reset = () => {
    setRunning(false);
    setRemaining(duration);
    setFinished(false);
  };
  const mmss = `${Math.floor(remaining / 60)}:${String(remaining % 60).padStart(2, "0")}`;
  const done = finished;

  const btn = light
    ? "bg-stone-100 hover:bg-stone-200 text-stone-600"
    : "bg-white/10 hover:bg-white/20 text-white/70";

  const flashing = done && timerFlash && flashOn;

  return (
    <div
      className={`${light ? "bg-stone-100" : "bg-white/[0.06]"} rounded-2xl p-6 text-center shrink-0 relative ring-2 transition-shadow ${
        flashing ? "ring-terracotta" : "ring-transparent"
      }`}
    >
      <button
        onClick={onCollapse}
        aria-label={t("cook.hideTimer")}
        className={`absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center ${btn} cursor-pointer`}
      >
        <X className="w-4 h-4" />
      </button>
      <p className={`text-xs ${light ? "text-stone-400" : "text-white/40"} font-semibold uppercase tracking-wide mb-2`}>{t("cook.timer")}</p>
      <p
        className="font-mono text-5xl font-medium leading-none tracking-tight mb-4"
        style={done ? { color: ACCENT } : undefined}
      >
        {mmss}
      </p>

      <div className="flex items-center justify-center gap-2 mb-4">
        <button
          onPointerDown={() => startAdjust(-30)}
          onPointerUp={stopAdjust}
          onPointerLeave={stopAdjust}
          onPointerCancel={stopAdjust}
          disabled={running}
          className={`px-3 h-8 rounded-lg ${btn} text-xs font-bold disabled:opacity-30 cursor-pointer disabled:cursor-default`}
        >
          {t("cook.minus30")}
        </button>
        <button
          onPointerDown={() => startAdjust(30)}
          onPointerUp={stopAdjust}
          onPointerLeave={stopAdjust}
          onPointerCancel={stopAdjust}
          disabled={running}
          className={`px-3 h-8 rounded-lg ${btn} text-xs font-bold disabled:opacity-30 cursor-pointer disabled:cursor-default`}
        >
          {t("cook.plus30")}
        </button>
      </div>

      <div className="flex items-center justify-center gap-3">
        <button
          onClick={() => {
            ensureAudio(); // unlock audio on this gesture so a later beep can play
            setRunning((r) => !r);
          }}
          disabled={remaining === 0}
          className="w-14 h-14 rounded-full flex items-center justify-center transition-colors disabled:opacity-40 cursor-pointer disabled:cursor-default"
          style={{ background: ACCENT }}
          aria-label={running ? t("cook.pause") : t("cook.start")}
        >
          {running ? (
            <Pause className="w-6 h-6 text-white" fill="currentColor" stroke="none" />
          ) : (
            <Play className="w-7 h-7 text-white ml-0.5" fill="currentColor" stroke="none" />
          )}
        </button>
        <button
          onClick={reset}
          className={`w-10 h-10 rounded-full ${btn} flex items-center justify-center cursor-pointer`}
          aria-label={t("cook.resetTimer")}
        >
          <RotateCcw className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}

// Vertical scroll for the ingredients list: hides the scrollbar and shows a down
// chevron signalling more content, hiding at the end (matches the home rails'
// chevrons). Clicking it scrolls roughly a page.
function IngredientsScroll({ light, children }: { light: boolean; children: React.ReactNode }) {
  const t = useT();
  const ref = useRef<HTMLDivElement>(null);
  const [canDown, setCanDown] = useState(false);

  const update = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    // -1 tolerance for sub-pixel rounding at the far end.
    setCanDown(Math.ceil(scrollTop + clientHeight) < scrollHeight - 1);
  }, []);

  // Watch container size for the mounted lifetime.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [update]);

  // Recompute after every render so it also reacts to content changes (scaling,
  // step navigation adopting a different recipe, etc.).
  useEffect(() => {
    update();
  });

  const nudge = (dir: 1 | -1) => {
    const el = ref.current;
    if (el) el.scrollBy({ top: dir * el.clientHeight * 0.8, behavior: "smooth" });
  };

  const btn = `absolute left-1/2 -translate-x-1/2 z-10 w-8 h-8 flex items-center justify-center rounded-full backdrop-blur-sm transition-colors cursor-pointer ${
    light
      ? "bg-stone-200 text-stone-600 hover:bg-stone-300"
      : "bg-white/15 text-white/80 hover:bg-white/25"
  }`;

  return (
    <div className="relative flex-1 min-h-0">
      <div
        ref={ref}
        onScroll={update}
        className="flex flex-col gap-1.5 h-full overflow-y-auto pr-3 no-scrollbar"
      >
        {children}
      </div>
      {canDown && (
        <button type="button" aria-label={t("cook.scrollIngredientsDown")} onClick={() => nudge(1)} className={`${btn} bottom-0`}>
          <ChevronDown className="w-5 h-5" />
        </button>
      )}
    </div>
  );
}
